import React, { useState, useEffect } from "react";
import { 
    Card, Row, Col, Form, Button, Spinner, Alert,
    InputGroup, Badge
} from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatDate } from "../utils/formatters";

export default function NouveauReglement() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const factureId = searchParams.get('facture');
    const clientId = searchParams.get('client');
    
    const [loading, setLoading] = useState(false);
    const [loadingData, setLoadingData] = useState(!!factureId || !!clientId);
    const [clients, setClients] = useState([]);
    const [factures, setFactures] = useState([]);
    const [searchClient, setSearchClient] = useState('');
    const [showClientResults, setShowClientResults] = useState(false);
    const [formData, setFormData] = useState({
        id_client: clientId || '',
        client_nom: '',
        id_facture: factureId || '',
        date_reglement: new Date().toISOString().split('T')[0],
        montant: '',
        mode_reglement: 'especes',
        reference: '',
        commentaire: ''
    });
    const [errors, setErrors] = useState({});
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [clientInfo, setClientInfo] = useState(null);
    const [factureInfo, setFactureInfo] = useState(null);

    // Charger les clients au montage
    useEffect(() => {
        fetchClients();
    }, []);

    // Charger les données si clientId ou factureId est présent dans l'URL
    useEffect(() => {
        if (clientId) {
            loadClientById(clientId);
        }
        if (factureId) {
            loadFactureById(factureId);
        }
    }, [clientId, factureId]);

    // Charger les factures impayées quand un client est sélectionné
    useEffect(() => {
        if (formData.id_client) {
            fetchFacturesImpayees(formData.id_client);
        }
    }, [formData.id_client]);

    const loadClientById = async (id) => {
        try {
            setLoadingData(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `https://sawd.julesferry-ga.com/api/clients.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                setClientInfo(result.data);
                setFormData(prev => ({
                    ...prev,
                    id_client: result.data.id_client,
                    client_nom: result.data.raison_sociale
                }));
                setSearchClient(result.data.raison_sociale);
            }
        } catch (err) {
            console.error("Erreur chargement client", err);
        } finally {
            setLoadingData(false);
        }
    };

    const loadFactureById = async (id) => {
        try {
            setLoadingData(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `https://sawd.julesferry-ga.com/api/factures.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                setFactureInfo(result.data);
                // Suggérer le montant du solde
                if (result.data && result.data.solde) {
                    setFormData(prev => ({
                        ...prev,
                        montant: result.data.solde
                    }));
                }
            }
        } catch (err) {
            console.error("Erreur chargement facture", err);
        } finally {
            setLoadingData(false);
        }
    };

    const fetchClients = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch('https://sawd.julesferry-ga.com/api/clients.php', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const result = await response.json();
            setClients(result.data || []);
        } catch (err) {
            console.error("Erreur chargement clients", err);
        }
    };

    const fetchFacturesImpayees = async (idClient) => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `https://sawd.julesferry-ga.com/api/reglements.php?action=factures_impayees&id_client=${idClient}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            const result = await response.json();
            setFactures(result.data || []);
            
            // Si une facture était pré-sélectionnée dans l'URL
            if (factureId) {
                setFormData(prev => ({ ...prev, id_facture: factureId }));
            }
        } catch (err) {
            console.error("Erreur chargement factures", err);
        }
    };

    const handleClientSelect = (client) => {
        setFormData({
            ...formData,
            id_client: client.id_client,
            client_nom: client.raison_sociale,
            id_facture: '' // Réinitialiser la facture quand on change de client
        });
        setSearchClient(client.raison_sociale);
        setShowClientResults(false);
        setClientInfo(client);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
        
        if (errors[name]) {
            setErrors({ ...errors, [name]: null });
        }
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.id_client) newErrors.id_client = "Veuillez sélectionner un client";
        if (!formData.montant) newErrors.montant = "Le montant est requis";
        else if (parseFloat(formData.montant) <= 0) newErrors.montant = "Le montant doit être supérieur à 0";
        if (!formData.mode_reglement) newErrors.mode_reglement = "Le mode de règlement est requis";
        
        if (formData.id_facture) {
            const facture = factures.find(f => f.id_facture === parseInt(formData.id_facture));
            if (facture && parseFloat(formData.montant) > facture.solde) {
                newErrors.montant = `Le montant ne peut pas dépasser le solde de la facture (${formatCurrency(facture.solde)})`;
            }
        }
        
        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const newErrors = validateForm();
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setLoading(true);
        setSubmitError('');

        try {
            const token = localStorage.getItem("token");
            const user = JSON.parse(localStorage.getItem("user") || "{}");

            const response = await fetch('https://sawd.julesferry-ga.com/api/reglements.php', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    action: 'create',
                    ...formData,
                    montant: parseFloat(formData.montant),
                    id_utilisateur: user.id_utilisateur || 1
                })
            });

            const result = await response.json();

            if (result.success) {
                setSubmitSuccess(true);
                setTimeout(() => {
                    navigate('/ventes/reglements');
                }, 1500);
            } else {
                setSubmitError(result.message || "Erreur lors de la création");
            }
        } catch (err) {
            setSubmitError("Erreur de connexion au serveur");
        } finally {
            setLoading(false);
        }
    };

    const factureSelectionnee = factures.find(f => f.id_facture === parseInt(formData.id_facture)) || factureInfo;

    if (loadingData) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                        <Spinner animation="border" variant="primary" />
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="container-fluid">
                    {/* En-tête */}
                    <Row className="mb-4">
                        <Col md={12}>
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h1 className="page-title mb-1">Nouveau règlement</h1>
                                    <p className="text-muted">
                                        {factureId ? 'Règlement pour une facture spécifique' : 'Saisissez un nouveau paiement client'}
                                    </p>
                                </div>
                                <Button variant="outline-secondary" onClick={() => navigate('/ventes/reglements')}>
                                    <i className="ri-arrow-left-line me-2"></i>Retour
                                </Button>
                            </div>
                        </Col>
                    </Row>

                    {/* Bannière d'information si pré-rempli */}
                    {(factureId || clientId) && (
                        <Alert variant="info" className="mb-4">
                            <div className="d-flex align-items-center">
                                <i className="ri-information-line fs-4 me-3"></i>
                                <div>
                                    <strong>Formulaire pré-rempli</strong>
                                    <p className="mb-0 small">
                                        {clientInfo && `Client: ${clientInfo.raison_sociale} `}
                                        {factureInfo && `- Facture: ${factureInfo.numero_facture} (Solde: ${formatCurrency(factureInfo.solde)})`}
                                    </p>
                                </div>
                            </div>
                        </Alert>
                    )}

                    {submitSuccess && (
                        <Alert variant="success" className="mb-4">
                            <i className="ri-checkbox-circle-line me-2"></i>
                            Règlement créé avec succès ! Redirection...
                        </Alert>
                    )}

                    {submitError && (
                        <Alert variant="danger" className="mb-4">
                            <i className="ri-error-warning-line me-2"></i>
                            {submitError}
                        </Alert>
                    )}

                    <Card className="border-0 shadow-sm">
                        <Card.Body className="p-4">
                            <Form onSubmit={handleSubmit}>
                                <Row>
                                    <Col md={6}>
                                       
                                        {/* Recherche client */}
                                        <Form.Group className="mb-3 position-relative">
                                            <Form.Label>Client <span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="Rechercher un client..."
                                                value={searchClient}
                                                onChange={(e) => {
                                                    setSearchClient(e.target.value);
                                                    setShowClientResults(true);
                                                }}
                                                onFocus={() => setShowClientResults(true)}
                                                isInvalid={!!errors.id_client}
                                                disabled={!!clientId} // Désactiver si client pré-sélectionné
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.id_client}
                                            </Form.Control.Feedback>
                                            
                                            {/* Résultats de recherche */}
                                            {showClientResults && searchClient && !clientId && (
                                                <div className="position-absolute w-100 bg-white border rounded-3 shadow-sm mt-1" style={{ zIndex: 1000, maxHeight: '300px', overflowY: 'auto' }}>
                                                    {clients
                                                        .filter(c => 
                                                            c.raison_sociale.toLowerCase().includes(searchClient.toLowerCase()) ||
                                                            c.code_client.toLowerCase().includes(searchClient.toLowerCase())
                                                        )
                                                        .map(client => (
                                                            <div
                                                                key={client.id_client}
                                                                className="p-3 border-bottom hover-bg-light"
                                                                onClick={() => handleClientSelect(client)}
                                                                style={{ cursor: 'pointer' }}
                                                            >
                                                                <strong>{client.raison_sociale}</strong>
                                                                <div className="small text-muted">
                                                                    {client.code_client} | {client.telephone || 'Tél non renseigné'}
                                                                </div>
                                                            </div>
                                                        ))}
                                                </div>
                                            )}
                                        </Form.Group>

                                        {/* Infos client sélectionné */}
                                        {formData.id_client && (
                                            <Alert variant="info" className="mb-3">
    <div className="d-flex justify-content-between align-items-center w-100">
        <div>
            <strong>{formData.client_nom}</strong>
            <div className="small">
                Code: {clients.find(c => c.id_client === parseInt(formData.id_client))?.code_client}
            </div>
        </div>
        {!clientId && (
            <Button 
                variant="outline-secondary" 
                size="sm"
                className="flex-shrink-0"  // Empêche le bouton de rétrécir
                onClick={() => {
                    setFormData({ ...formData, id_client: '', client_nom: '', id_facture: '' });
                    setSearchClient('');
                    setFactures([]);
                }}
            >
                <i className="ri-close-line"></i>
            </Button>
        )}
    </div>
</Alert>
                                        )}
                                    </Col>

                                    <Col md={6}>
                                        <h5 className="mb-3">Informations de paiement</h5>
                                        
                                        <Form.Group className="mb-3">
                                            <Form.Label>Date de règlement <span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                type="date"
                                                name="date_reglement"
                                                value={formData.date_reglement}
                                                onChange={handleChange}
                                                required
                                            />
                                        </Form.Group>

                                        <Form.Group className="mb-3">
                                            <Form.Label>Mode de règlement <span className="text-danger">*</span></Form.Label>
                                            <Form.Select
                                                name="mode_reglement"
                                                value={formData.mode_reglement}
                                                onChange={handleChange}
                                                isInvalid={!!errors.mode_reglement}
                                                required
                                            >
                                                <option value="especes">Espèces</option>
                                                <option value="cheque">Chèque</option>
                                                <option value="virement">Virement bancaire</option>
                                                <option value="carte_bancaire">Carte bancaire</option>
                                                <option value="traite">Traite</option>
                                            </Form.Select>
                                            <Form.Control.Feedback type="invalid">
                                                {errors.mode_reglement}
                                            </Form.Control.Feedback>
                                        </Form.Group>

                                        <Form.Group className="mb-3">
                                            <Form.Label>Référence (N° chèque, virement...)</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="reference"
                                                value={formData.reference}
                                                onChange={handleChange}
                                                placeholder="Optionnel"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row className="mt-0">
                                    <Col md={6}>
                                       
                                        <Form.Group className="mb-3">
                                            <Form.Label>Facture</Form.Label>
                                            <Form.Select
                                                name="id_facture"
                                                value={formData.id_facture}
                                                onChange={handleChange}
                                                disabled={!formData.id_client || factures.length === 0 || !!factureId}
                                            >
                                                <option value="">Sélectionnez une facture (optionnel)</option>
                                                {factures.map(facture => (
                                                    <option key={facture.id_facture} value={facture.id_facture}>
                                                        {facture.numero_facture} - {formatCurrency(facture.solde)} restant
                                                    </option>
                                                ))}
                                            </Form.Select>
                                            {!formData.id_client && (
                                                <Form.Text className="text-muted">
                                                    Sélectionnez d'abord un client
                                                </Form.Text>
                                            )}
                                        </Form.Group>

                                       {factureSelectionnee && (
    <div className="facture-summary-card mb-4">
        <div className="summary-header">
            <i className="ri-file-list-3-line me-2"></i>
            <span>Facture associée</span>
        </div>
        
        <div className="summary-body">
            <div className="summary-row">
                <span className="summary-label">
                    <i className="ri-barcode-line me-2 text-secondary"></i>
                    Numéro
                </span>
                <span className="summary-value fw-semibold">{factureSelectionnee.numero_facture}</span>
            </div>
            
            <div className="summary-row">
                <span className="summary-label">
                    <i className="ri-calculator-line me-2 text-secondary"></i>
                    Montant TTC
                </span>
                <span className="summary-value fw-semibold">{formatCurrency(factureSelectionnee.montant_ttc)}</span>
            </div>
            
            <div className="summary-row">
                <span className="summary-label">
                    <i className="ri-bank-card-line me-2 text-secondary"></i>
                    Déjà réglé
                </span>
                <span className="summary-value text-success fw-semibold">
                    <i className="ri-checkbox-circle-line me-1"></i>
                    {formatCurrency(factureSelectionnee.montant_regle || 0)}
                </span>
            </div>
            
            <div className="summary-divider"></div>
            
            <div className="summary-row highlight">
                <span className="summary-label fw-bold">
                    <i className="ri-funds-line me-2 text-primary"></i>
                    Solde restant
                </span>
                <span className="summary-value text-primary fw-bold fs-5">
                    {formatCurrency(factureSelectionnee.solde)}
                </span>
            </div>
        </div>
    </div>
)}
                                    </Col>

                                    <Col md={6}>
                                        
                                        <Form.Group className="mb-3">
                                            <Form.Label>Montant <span className="text-danger">*</span></Form.Label>
                                            <InputGroup>
                                                <InputGroup.Text>FCFA</InputGroup.Text>
                                                <Form.Control
                                                    type="number"
                                                    name="montant"
                                                    value={formData.montant}
                                                    onChange={handleChange}
                                                    placeholder="0"
                                                    min="0"
                                                    step="0.01"
                                                    isInvalid={!!errors.montant}
                                                    required
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.montant}
                                                </Form.Control.Feedback>
                                            </InputGroup>
                                            {factureSelectionnee && (
                                                <Form.Text className="text-muted">
                                                    Solde de la facture: {formatCurrency(factureSelectionnee.solde)}
                                                </Form.Text>
                                            )}
                                        </Form.Group>

                                        <Form.Group className="mb-3">
                                            <Form.Label>Commentaire</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                name="commentaire"
                                                value={formData.commentaire}
                                                onChange={handleChange}
                                                rows={3}
                                                placeholder="Informations complémentaires..."
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <div className="d-flex justify-content-end gap-2 mt-4">
                                    <Button 
                                        variant="outline-secondary" 
                                        onClick={() => navigate('/ventes/reglements')}
                                    >
                                        Annuler
                                    </Button>
                                    <Button 
                                        type="submit" 
                                        variant="primary"
                                        disabled={loading}
                                    >
                                        {loading ? <Spinner size="sm" /> : 'Enregistrer le règlement'}
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                </div>
                <Footer />

                 <style>{`

.facture-summary-card {
    background: linear-gradient(135deg, #f8faff 0%, #ffffff 100%);
    border: 1px solid #e9ecef;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 8px 20px rgba(0,0,0,0.05);
    transition: all 0.3s ease;
}

.facture-summary-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 25px rgba(67, 97, 238, 0.1);
}

.summary-header {
    background: linear-gradient(135deg, #4361ee15, #3a56d415);
    padding: 12px 16px;
    border-bottom: 1px solid #e9ecef;
    font-weight: 600;
    color: #2c3e50;
    display: flex;
    align-items: center;
}

.summary-header i {
    color: #4361ee;
    font-size: 1.2rem;
}

.summary-body {
    padding: 16px;
}

.summary-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px dashed #e9ecef;
}

.summary-row:last-of-type {
    border-bottom: none;
}

.summary-row.highlight {
    background: #f1f5f9;
    margin-top: 8px;
    padding: 12px 16px;
    border-radius: 12px;
    border-bottom: none;
}

.summary-label {
    display: flex;
    align-items: center;
    color: #5a6a7e;
    font-size: 0.9rem;
}

.summary-label i {
    font-size: 1rem;
    color: #64748b;
}

.summary-value {
    font-size: 1rem;
}

.summary-divider {
    height: 1px;
    background: linear-gradient(90deg, transparent, #4361ee30, transparent);
    margin: 12px 0;
}

/* Animation d'entrée */
.facture-summary-card {
    animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
    from {
        opacity: 0;
        transform: translateY(-10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

/* Variante plus compacte si nécessaire */
.facture-summary-card.compact {
    border-radius: 12px;
}

.facture-summary-card.compact .summary-body {
    padding: 12px;
}

.facture-summary-card.compact .summary-row {
    padding: 6px 0;
}

 `}</style>

            </div>  
        </>
    );
}